import * as React from 'react'
import useLocale from 'antd/es/locale/useLocale'
import dayjs, { Dayjs } from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isoWeek from 'dayjs/plugin/isoWeek'
import localeData from 'dayjs/plugin/localeData'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import quarterOfYear from 'dayjs/plugin/quarterOfYear'
import weekday from 'dayjs/plugin/weekday'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import weekYear from 'dayjs/plugin/weekYear'

// antd's OWN locale hook and pack, not transcribed copies — the same choice
// `../Input.shadcn` makes for `DisabledContext`. `useLocale` is what reads the
// app-wide `<ConfigProvider locale>` that `src/App/AntdI18n` sets from the
// user's `me.datetimeFormatLocale`, so the shadcn panel and antd's own
// DatePicker resolve the SAME pack for the same tenant. The ES subpath is
// load-bearing: `antd/lib/*` would be a second, always-default copy of that
// context (AGENTS.md, the ES/CJS split-context trap).
import type { PickerLocale } from 'src/antd/locale'
import enUS from 'src/antd/locale/packs/en_US'

export type { PickerLocale }

/**
 * antd locale-pack code (`en_GB`, `da_DK`, …) → dayjs locale name.
 *
 * Transcribed from rc-picker 4.x `generate/dayjs.ts` (`localeMap` +
 * `parseLocale`), which is what antd's own DatePicker uses to decide which
 * dayjs locale formats the input text and the panel. Keeping the same mapping
 * is what makes `locale={enUS.DatePicker}` still mean "English-pinned" (see
 * the Overview FilterBar) after the migration.
 */
const LOCALE_MAP: Record<string, string> = {
  // ar_EG:
  ar_EG: 'ar',
  az_AZ: 'az',
  bg_BG: 'bg',
  bn_BD: 'bn-bd',
  by_BY: 'be',
  ca_ES: 'ca',
  cs_CZ: 'cs',
  da_DK: 'da',
  de_DE: 'de',
  el_GR: 'el',
  en_GB: 'en-gb',
  en_US: 'en',
  es_ES: 'es',
  et_EE: 'et',
  fa_IR: 'fa',
  fi_FI: 'fi',
  fr_BE: 'fr',
  fr_CA: 'fr-ca',
  fr_FR: 'fr',
  ga_IE: 'ga',
  gl_ES: 'gl',
  he_IL: 'he',
  hi_IN: 'hi',
  hr_HR: 'hr',
  hu_HU: 'hu',
  hy_AM: 'hy-am',
  id_ID: 'id',
  is_IS: 'is',
  it_IT: 'it',
  ja_JP: 'ja',
  ka_GE: 'ka',
  kk_KZ: 'kk',
  km_KH: 'km',
  kn_IN: 'kn',
  ko_KR: 'ko',
  ku_IQ: 'ku',
  lt_LT: 'lt',
  lv_LV: 'lv',
  mk_MK: 'mk',
  ml_IN: 'ml',
  mn_MN: 'mn',
  ms_MY: 'ms-my',
  nb_NO: 'nb',
  ne_NP: 'ne',
  nl_BE: 'nl-be',
  nl_NL: 'nl',
  pl_PL: 'pl',
  pt_BR: 'pt-br',
  pt_PT: 'pt',
  ro_RO: 'ro',
  ru_RU: 'ru',
  si_LK: 'si',
  sk_SK: 'sk',
  sl_SI: 'sl',
  sr_RS: 'sr',
  sv_SE: 'sv',
  ta_IN: 'ta',
  th_TH: 'th',
  tk_TK: 'tk',
  tr_TR: 'tr',
  uk_UA: 'uk',
  ur_PK: 'ur',
  vi_VN: 'vi',
  zh_CN: 'zh-cn',
  zh_HK: 'zh-hk',
  zh_TW: 'zh-tw',
}

// The dayjs plugins the whole seam depends on, registered HERE rather than
// left to src/index.tsx.
//
// rc-picker's dayjs adapter (`rc-picker/lib/generate/dayjs`) used to extend
// most of these as an import side effect, which is why `picker="week"`
// (`gggg-wo`), `picker="quarter"` and the ISO week-number column worked even
// though the app's bootstrap never registered `advancedFormat`, `weekYear` or
// `quarterOfYear`. Nothing imports that adapter once the seam renders the
// panels, so the seam owns the registration — and `isoWeek` in particular is a
// hard crash in the week-number column without it, not a formatting nicety.
// `dayjs.extend` is idempotent, so re-registering what src/index.tsx already
// added is free, and it is what lets a picker render in an isolated unit test.
dayjs.extend(customParseFormat)
dayjs.extend(advancedFormat)
dayjs.extend(isoWeek)
dayjs.extend(localeData)
dayjs.extend(localizedFormat)
dayjs.extend(quarterOfYear)
dayjs.extend(weekday)
dayjs.extend(weekOfYear)
dayjs.extend(weekYear)

export const parseLocale = (locale: string): string =>
  LOCALE_MAP[locale] ?? locale.split('_')[0]

/**
 * Resolve the picker locale exactly the way antd does: an explicit `locale`
 * prop wins, otherwise the app-wide `ConfigProvider locale.DatePicker`
 * (src/App/AntdI18n wires it from `me.datetimeFormatLocale`), otherwise en_US.
 *
 * antd reads the key `'DatePicker'` for a single picker but `'Calendar'` for a
 * range one (`generateSinglePicker` / `generateRangePicker`, 5.15.4) — a quirk
 * with no consequence: every antd locale pack assigns ONE object to both keys
 * (`Calendar === DatePicker` in en_US, en_GB, da_DK and fi_FI alike), so one
 * hook serves both halves of this seam.
 */
export const usePickerLocale = (localeProp?: PickerLocale): PickerLocale => {
  const [contextLocale] = useLocale(
    'DatePicker',
    enUS.DatePicker as PickerLocale
  )
  return React.useMemo(
    () => ({ ...contextLocale, ...localeProp }),
    [contextLocale, localeProp]
  )
}

/**
 * The dayjs locale name to format, parse and lay the week out with, or
 * `undefined` for "whatever `dayjs.locale()` is globally".
 *
 * IT TAKES THE RESOLVED LOCALE, NOT THE PROP — a deliberate divergence from
 * `update-design`, which pins only when a call site passes `locale` explicitly
 * and otherwise follows the global dayjs locale. That reads like a nicety and
 * is not: on this branch the two sources DISAGREE for a user who has set no
 * date-format preference. `src/App/AntdI18n` defaults `locale.DatePicker` to
 * the `en_GB` pack, while `src/App/DayjsI18n` leaves the global dayjs locale at
 * `en` — and `en` starts its week on SUNDAY where `en_GB` starts it on MONDAY,
 * and writes `LL` as "July 21, 2026" rather than "21 July 2026". Following the
 * global locale would therefore have moved a column and changed a date string
 * for every such user the moment the toggle was flipped.
 *
 * Resolving from the merged locale is also exactly what antd does: rc-picker
 * hands `locale.locale` (`'da_DK'`, `'fi_FI'`, …) to its dayjs adapter, which
 * runs the same `parseLocale` map before every `format`, `parse`,
 * `getWeekFirstDay`, `getShortWeekDays` and `getShortMonths`
 * (`rc-picker/es/generate/dayjs.js`, and `PickerPanel/DatePanel` for the
 * callers). So the seam and antd's own panel now answer identically at every
 * moment — INCLUDING while a lazily-imported pack is still in flight, where
 * both briefly show en_GB (`useAntdLocalePack` says so in its own comment).
 * Removing that flash is a real improvement and a separate decision; it is not
 * this port's to make.
 */
export const useDayjsLocaleName = (locale?: PickerLocale): string | undefined =>
  React.useMemo(
    () => (locale?.lang?.locale ? parseLocale(locale.lang.locale) : undefined),
    [locale]
  )

/** Apply a pinned dayjs locale, or leave the value on the global one. */
export const withLocale = (value: Dayjs, localeName?: string): Dayjs =>
  localeName ? value.locale(localeName) : value

/** `dayjs.localeData()` for the active (pinned or global) locale. */
export const localeDataFor = (localeName?: string) =>
  withLocale(dayjs(), localeName).localeData()
